import { type Handle, css } from 'remix/ui'
import { formatTimestampDate } from '#client/format-timestamp.ts'
import { navigate, readCurrentRouterHref } from '#client/client-router.tsx'
import { createDoubleCheck } from '#client/double-check.ts'
import { createListDetailRoute } from '#client/list-detail-route.ts'
import { replaceLocation } from '#client/replace-location.ts'
import { createRouteData, routeDataRedirect } from '#client/route-data.tsx'
import { matchesSearchQuery } from '#client/search-filter.ts'
import {
	type AccountStatus,
	readJson,
} from '#client/routes/account-approval-shared.ts'
import {
	routeLoaderRedirect,
	type RouteLoaderResult,
} from '#client/route-loader.ts'
import {
	AccountManagementMessage,
	AccountManagementShell,
	AccountPageHeader,
	MetadataGrid,
	TimestampValue,
	accountInputCss,
	accountTextareaCss,
} from '#client/routes/account-management-components.tsx'
import {
	RecordTable,
	RecordTableSearch,
	recordBodyCss,
	recordCellClamp,
	recordStampCss,
} from '#client/routes/record-table.tsx'
import { colors, spacing, typography } from '#universal/styles/tokens.ts'
import {
	cardTitleCss,
	descriptionCss,
	fieldCss,
	fieldLabelCss,
	getDangerPillCss,
} from '#universal/styles/style-primitives.ts'
import {
	type AccountValueDetail,
	type AccountValueListItem,
	type AccountValuesLoaderData,
} from '#universal/loader-data.ts'

const clampedCellCss = css(recordCellClamp(28))

type EditorState = {
	name: string
	description: string
	value: string
}

const accountValuesApiPath = '/account/values.json'
const accountValuesPath = '/account/values'
const valuesRoute = createListDetailRoute(accountValuesPath)

/**
 * Selection changes need a refetch for full `selectedValue`. The client-side
 * `q` filter does not change the GET response, so it is omitted from the key.
 */
function getDataLatchKey(href: string) {
	return new URL(href, 'http://localhost').pathname
}

function readSearchFilter(href: string) {
	return new URL(href, 'http://localhost').searchParams.get('q')?.trim() ?? ''
}

function selectionSyncKey(selection: {
	selectedId: string | null
	isCreating: boolean
}) {
	if (selection.isCreating) return 'new'
	if (selection.selectedId) return `id:${selection.selectedId}`
	return 'none'
}

function filterValues(values: Array<AccountValueListItem>, search: string) {
	return values.filter((entry) =>
		matchesSearchQuery(search, [
			entry.name,
			entry.description,
			entry.valuePreview,
		]),
	)
}

function createEmptyEditorState(): EditorState {
	return {
		name: '',
		description: '',
		value: '',
	}
}

function createEditorStateFromDetail(detail: AccountValueDetail): EditorState {
	return {
		name: detail.name,
		description: detail.description,
		value: detail.value,
	}
}

function buildValuesApiRequestUrl(href: string) {
	const selection = valuesRoute.getSelection(href)
	const requestUrl = new URL(accountValuesApiPath, 'http://localhost')
	if (selection.selectedId) {
		requestUrl.searchParams.set('selected', selection.selectedId)
	}
	return `${requestUrl.pathname}${requestUrl.search}`
}

function formatRelativeTtl(ttlMs: number | null) {
	if (ttlMs == null) return 'No expiry'
	const totalMinutes = Math.max(1, Math.round(ttlMs / 60_000))
	if (totalMinutes < 60) return `${totalMinutes}m`
	const hours = Math.floor(totalMinutes / 60)
	const minutes = totalMinutes % 60
	if (hours < 48) {
		return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`
	}
	const days = Math.floor(hours / 24)
	return `${days}d`
}

export async function accountValuesRouteLoader(
	url: URL,
	signal: AbortSignal,
): Promise<RouteLoaderResult> {
	const href = `${url.pathname}${url.search}`
	const response = await fetch(buildValuesApiRequestUrl(href), {
		headers: { Accept: 'application/json' },
		credentials: 'include',
		signal,
	})
	if (response.status === 401) {
		return routeLoaderRedirect('/login')
	}
	const payload = await readJson<AccountValuesLoaderData>(response)
	if (!response.ok || !payload?.ok) {
		throw new Error('Unable to load values.')
	}
	return { accountValues: payload }
}

export function AccountValuesRoute(handle: Handle) {
	let saveState: 'idle' | 'deleting' = 'idle'
	let values: Array<AccountValueListItem> = []
	let selectedValue: AccountValueDetail | null = null
	let editorState = createEmptyEditorState()
	let message: string | null = null
	let messageTone: 'info' | 'error' = 'info'
	/** Payload last applied to the closure state above. */
	let appliedPayload: AccountValuesLoaderData | null = null
	let appliedError: Error | null = null
	const deleteValueCheck = createDoubleCheck(handle)
	let syncedSelectionKey: string | null = null
	const valuesData = createRouteData({
		key: 'accountValues',
		locationKey: getDataLatchKey,
		async load(href, signal) {
			const response = await fetch(buildValuesApiRequestUrl(href), {
				headers: { Accept: 'application/json' },
				credentials: 'include',
				signal,
			})
			if (response.status === 401) return routeDataRedirect('/login')
			const payload = await readJson<AccountValuesLoaderData>(response)
			if (!response.ok || !payload?.ok) {
				throw new Error('Unable to load values.')
			}
			return payload
		},
	})

	const dangerButtonCss = getDangerPillCss({ size: 'sm' })

	function getCurrentHref() {
		return readCurrentRouterHref(handle)
	}

	function getCurrentSearch() {
		return new URL(getCurrentHref(), 'http://localhost').search
	}

	function syncRouterLocation(nextPath: string) {
		navigate(nextPath)
	}

	function buildHrefWithUpdatedSearch(search: string) {
		const nextUrl = new URL(getCurrentHref(), 'http://localhost')
		if (search) nextUrl.searchParams.set('q', search)
		else nextUrl.searchParams.delete('q')
		return `${nextUrl.pathname}${nextUrl.search}`
	}

	function setMessage(
		nextMessage: string | null,
		tone: 'info' | 'error' = 'info',
	) {
		message = nextMessage
		messageTone = tone
	}

	function syncEditorToSelection(selection: {
		selectedId: string | null
		isCreating: boolean
	}) {
		const nextKey = selectionSyncKey(selection)
		if (nextKey === syncedSelectionKey) return
		syncedSelectionKey = nextKey
		deleteValueCheck.reset()
		if (selection.isCreating) {
			editorState = createEmptyEditorState()
			selectedValue = null
			return
		}
		if (selection.selectedId) {
			if (selectedValue && selectedValue.id === selection.selectedId) {
				editorState = createEditorStateFromDetail(selectedValue)
				return
			}
			selectedValue = null
			editorState = {
				name: selection.selectedId,
				description: '',
				value: '',
			}
			return
		}
		selectedValue = null
		editorState = createEmptyEditorState()
	}

	function applyPayload(payload: AccountValuesLoaderData) {
		values = payload.values
		selectedValue = payload.selectedValue
		deleteValueCheck.reset()
		if (payload.selectedValue) {
			editorState = createEditorStateFromDetail(payload.selectedValue)
			syncedSelectionKey = selectionSyncKey(
				valuesRoute.getSelection(getCurrentHref()),
			)
			return
		}
		syncedSelectionKey = null
		syncEditorToSelection(valuesRoute.getSelection(getCurrentHref()))
	}

	async function deleteValueEntry() {
		if (!selectedValue || saveState !== 'idle') return
		saveState = 'deleting'
		setMessage(null)
		handle.update()
		try {
			const response = await fetch(accountValuesApiPath, {
				method: 'POST',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				credentials: 'include',
				body: JSON.stringify({
					action: 'delete',
					name: selectedValue.name,
				}),
			})
			if (response.status === 401) {
				window.location.assign('/login')
				return
			}
			const payload = await readJson<
				AccountValuesLoaderData & { error?: string; ok?: boolean }
			>(response)
			if (!response.ok || !payload?.ok) {
				throw new Error(payload?.error || 'Unable to delete value.')
			}
			applyPayload(payload)
			selectedValue = null
			editorState = createEmptyEditorState()
			syncedSelectionKey = 'none'
			deleteValueCheck.reset()
			saveState = 'idle'
			setMessage('Deleted value.')
			syncRouterLocation(valuesRoute.buildListHref(getCurrentSearch()))
			handle.update()
		} catch (error) {
			saveState = 'idle'
			setMessage(
				error instanceof Error ? error.message : 'Unable to delete value.',
				'error',
			)
			handle.update()
		}
	}

	function resetSelectionState() {
		deleteValueCheck.reset()
		setMessage(null)
		handle.update()
	}

	return () => {
		const currentHref = getCurrentHref()
		const snapshot = valuesData.read(handle, currentHref)
		if (snapshot.data && snapshot.data !== appliedPayload) {
			appliedPayload = snapshot.data
			applyPayload(snapshot.data)
			if (messageTone === 'error') setMessage(null)
		}
		if (snapshot.error && snapshot.error !== appliedError) {
			appliedError = snapshot.error
			setMessage(snapshot.error.message, 'error')
		}
		const pending = snapshot.kind === 'pending'
		const status: AccountStatus =
			snapshot.kind === 'error'
				? 'error'
				: pending && appliedPayload === null
					? 'loading'
					: 'ready'

		const selection = valuesRoute.getSelection(currentHref)
		syncEditorToSelection(selection)
		const search = readSearchFilter(currentHref)
		const filteredValues = filterValues(values, search)
		const isMutating = saveState !== 'idle'
		const detail = selectedValue
		const isLoadingSelection =
			selection.selectedId != null &&
			(detail == null || detail.id !== selection.selectedId) &&
			pending
		const showEditor =
			selection.selectedId != null && detail != null && !isLoadingSelection
		const showValueNotFound =
			selection.selectedId != null &&
			detail == null &&
			status === 'ready' &&
			!pending
		const selectedLabel = detail?.name ?? selection.selectedId ?? 'Value'

		const monospaceValueCss = {
			...accountTextareaCss,
			fontFamily: 'monospace',
			fontSize: typography.fontSize.sm,
			minHeight: '10rem',
		}

		return (
			<AccountManagementShell busy={pending && appliedPayload !== null}>
				<AccountPageHeader
					title="Values"
					description="Leftover named rows from an older storage model. New work uses secrets and package storage."
					currentHref={currentHref}
				/>

				{status === 'loading' ? (
					<p mix={css({ color: colors.textMuted, margin: 0 })}>
						Loading values…
					</p>
				) : null}
				{message ? (
					<AccountManagementMessage
						tone={
							status === 'error' || messageTone === 'error' ? 'error' : 'info'
						}
					>
						{message}
					</AccountManagementMessage>
				) : null}

				{status === 'ready' ? (
					<RecordTable
						mode="expand"
						ariaLabel="Saved values"
						selectedId={selection.selectedId}
						recordLoading={isLoadingSelection}
						onNavigate={resetSelectionState}
						countLabel={`${filteredValues.length} of ${values.length} shown`}
						emptyLabel={
							values.length === 0
								? 'No leftover rows.'
								: 'No leftover rows match the current filters.'
						}
						toolbar={
							<RecordTableSearch
								label="Search values"
								placeholder="Search values"
								value={search}
								onInput={(value) => {
									replaceLocation(buildHrefWithUpdatedSearch(value))
								}}
							/>
						}
						columns={[
							{ key: 'name', label: 'Name', primary: true },
							{ key: 'description', label: 'Description', drop: 1 },
							{ key: 'preview', label: 'Value', drop: 2 },
							{ key: 'updated', label: 'Updated' },
						]}
						rows={filteredValues.map((entry) => ({
							id: entry.id,
							// A save or delete is in flight; the expanded editor owns
							// the selection until it settles.
							href: isMutating
								? undefined
								: valuesRoute.buildDetailHref(entry.id, getCurrentSearch()),
							cells: {
								name: entry.name,
								description: (
									<span mix={clampedCellCss}>{entry.description || '—'}</span>
								),
								preview: (
									<span
										mix={[clampedCellCss, css({ fontFamily: 'monospace' })]}
									>
										{entry.valuePreview || '—'}
									</span>
								),
								updated: (
									<span mix={css(recordStampCss)}>
										{formatTimestampDate(entry.updatedAt)}
									</span>
								),
							},
						}))}
						record={
							isLoadingSelection ? (
								<p mix={css({ margin: 0, color: colors.textMuted })}>
									Loading value…
								</p>
							) : showEditor ? (
								<div mix={css(recordBodyCss)}>
									<div mix={css({ display: 'grid', gap: spacing.xs })}>
										<h2 mix={css(cardTitleCss)}>{selectedLabel}</h2>
										<p mix={css(descriptionCss)}>
											Read-only leftover row. Move the contents to memories,
											package storage, a repo, or secrets, then delete.
										</p>
									</div>

									<label mix={css(fieldCss)}>
										<span mix={css(fieldLabelCss)}>Name</span>
										<input
											data-field-ring
											name="name"
											type="text"
											value={editorState.name}
											readOnly
											mix={css({
												...accountInputCss,
												color: colors.textMuted,
												cursor: 'default',
											})}
										/>
									</label>

									<label mix={css(fieldCss)}>
										<span mix={css(fieldLabelCss)}>Description</span>
										<input
											data-field-ring
											name="description"
											type="text"
											value={editorState.description}
											readOnly
											mix={css({
												...accountInputCss,
												color: colors.textMuted,
												cursor: 'default',
											})}
										/>
									</label>

									<label mix={css(fieldCss)}>
										<span mix={css(fieldLabelCss)}>Value</span>
										<textarea
											data-field-ring
											name="value"
											value={editorState.value}
											readOnly
											mix={css({
												...monospaceValueCss,
												color: colors.textMuted,
												cursor: 'default',
											})}
										/>
									</label>

									{detail ? (
										<MetadataGrid
											items={[
												{
													label: 'Scope',
													value: detail.scope,
												},
												{
													label: 'Created',
													value: <TimestampValue value={detail.createdAt} />,
												},
												{
													label: 'Updated',
													value: <TimestampValue value={detail.updatedAt} />,
												},
												{
													label: 'TTL',
													value: formatRelativeTtl(detail.ttlMs),
												},
											]}
										/>
									) : null}

									<div
										mix={css({
											display: 'flex',
											gap: spacing.sm,
											flexWrap: 'wrap',
										})}
									>
										<button
											type="button"
											disabled={isMutating}
											mix={[
												...deleteValueCheck.getButtonMix({
													on: {
														click: () => void deleteValueEntry(),
													},
												}),
												css(dangerButtonCss),
											]}
										>
											{saveState === 'deleting'
												? 'Deleting...'
												: deleteValueCheck.doubleCheck
													? 'Confirm delete'
													: 'Delete'}
										</button>
									</div>
								</div>
							) : showValueNotFound ? (
								<div mix={css({ ...recordBodyCss, gap: spacing.sm })}>
									<h2
										mix={css({
											margin: 0,
											fontSize: typography.fontSize.lg,
											fontWeight: typography.fontWeight.semibold,
											color: colors.text,
										})}
									>
										Value not found
									</h2>
									<p mix={css({ margin: 0, color: colors.textMuted })}>
										This value does not exist for this account or is managed by
										another settings page.
									</p>
								</div>
							) : null
						}
					/>
				) : null}
			</AccountManagementShell>
		)
	}
}
